import dynamic from 'next/dynamic';
import { useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { IFacetOption } from 'shared/types/ConstructorFacet';
import { SearchFilterLink } from 'search/components/searchGrid/SearchFilterLink';
import { SearchFilterListOption } from 'search/styles/SearchStyles';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { getAppliedFilters } from 'shared/utils/gridEventTrackingUtils';
import { useEventContext } from 'tracking/hooks/useEventContext';
import {
  getConstructorCategoriesStrings,
  getConstructorDescription,
} from 'search/enums/translations';
import { startCase } from 'shared/utils/startCase';

const SearchFilterTooltip = dynamic(
  import('search/components/searchGrid/SearchFilterTooltip').then((mod) => mod.SearchFilterTooltip),
  { ssr: false },
);

interface ISearchFilterConditionOptionDesktop {
  facetType: string;
  option: IFacetOption;
  resultIds: Array<string>;
}

interface IConsumerSearchFilterConditionOptionDesktop {
  facetType: string;
  option: IFacetOption;
  handleClick: any;
}
export const ConsumerSearchFilterConditionOptionDesktop = ({
  facetType,
  option,
  handleClick,
}: IConsumerSearchFilterConditionOptionDesktop) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const eventTrackingEventContext = useEventContext();
  const { query } = useRouter();

  const handleMouseOver = () => setIsVisible(true);

  const handleMouseLeave = () => setIsVisible(false);

  const handleOnClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    sendTrackingEvent('FILTER_APPLY_TAP', {
      applied_filters: getAppliedFilters(query),
      event_context: eventTrackingEventContext,
      filter_name: facetType,
      filter_selection: option.value,
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query?.query.toString() }),
    });

    handleClick?.(option.value);
  };

  return (
    <li key={option.value}>
      <SearchFilterListOption
        $isSelected={option.status === 'selected'}
        data-qa="condition_filter_option"
        ref={ref}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        onClick={handleOnClick}
      >
        {startCase(getConstructorCategoriesStrings(option.display_name))}
        {option?.data?.description && (
          <SearchFilterTooltip
            data-qa="condition_filter_tooltip"
            isVisible={isVisible}
            text={getConstructorDescription(option.data.description)}
            triggerRef={ref}
          />
        )}
      </SearchFilterListOption>
    </li>
  );
};

export const SearchFilterConditionOptionDesktop = ({
  facetType,
  option,
  resultIds,
}: ISearchFilterConditionOptionDesktop) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);
  const eventTrackingEventContext = useEventContext();
  const { query } = useRouter();

  const handleMouseOver = () => setIsVisible(true);

  const handleMouseLeave = () => setIsVisible(false);

  const handleOnClick = () => {
    sendTrackingEvent('FILTER_APPLY_TAP', {
      applied_filters: getAppliedFilters(query),
      event_context: eventTrackingEventContext,
      filter_name: facetType,
      filter_selection: option.value,
      result_id: resultIds[0],
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(query?.query && { search_string: query?.query.toString() }),
    });
  };

  return (
    <li key={option.value}>
      <SearchFilterLink facetType={facetType} option={option}>
        <SearchFilterListOption
          $isSelected={option.status === 'selected'}
          data-qa="condition_filter_option"
          ref={ref}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onClick={handleOnClick}
        >
          {startCase(getConstructorCategoriesStrings(option.display_name))}
          {option?.data?.description && (
            <SearchFilterTooltip
              data-qa="condition_filter_tooltip"
              isVisible={isVisible}
              text={getConstructorDescription(option.data.description)}
              triggerRef={ref}
            />
          )}
        </SearchFilterListOption>
      </SearchFilterLink>
    </li>
  );
};
